import { isMosaicTrafficLightsValue, MosaicTrafficLightsValue } from "../../traffic-lights";
import { GridItemChangeType } from "./interface";

/**
 * Traffic light information associated with a grid item (row/column/group).
 */

export interface GridItemTrafficLight {
  /** Core traffic light value. */
  coreTrafficLight: MosaicTrafficLightsValue;
  /** The identifier for the grid item. */
  gridItemId: string;
  /** Optional id used after a traffic light is shown. */
  idAfterTrafficLightIsShown?: string;
  /** When true, the grid item is optional. */
  isOptional: boolean;
  /** The change type associated with the grid item. */
  gridItemChangeType: GridItemChangeType;
}

/**
 * Type guard that asserts whether a value conforms to the GridItemTrafficLight shape.
 *
 * @param obj - Unknown value to test. Typically an object parsed from JSON or coming from
 *        an external source where the exact type is not known.
 * @returns True when `obj` matches the `GridItemTrafficLight` interface; otherwise false.
 *          This function is a type predicate (`obj is GridItemTrafficLight`) so callers can
 *          narrow the value's type after a successful check.
 */
export const isGridItemTrafficLight = (obj: unknown): obj is GridItemTrafficLight => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;
  // Check properties exist
  if (
    !("coreTrafficLight" in obj) ||
    !("gridItemId" in obj) ||
    !("isOptional" in obj) ||
    !("gridItemChangeType" in obj)
  )
    return false;

  // Check basic property types
  if (
    typeof (obj as GridItemTrafficLight).coreTrafficLight !== "string" ||
    typeof (obj as GridItemTrafficLight).gridItemId !== "string" ||
    typeof (obj as GridItemTrafficLight).isOptional !== "boolean" ||
    typeof (obj as GridItemTrafficLight).gridItemChangeType !== "string"
  )
    return false;

  // Check optional property types
  if (
    (obj as GridItemTrafficLight).idAfterTrafficLightIsShown !== undefined &&
    typeof (obj as GridItemTrafficLight).idAfterTrafficLightIsShown !== "string"
  )
    return false;

  // Check enums
  if (!isMosaicTrafficLightsValue((obj as GridItemTrafficLight).coreTrafficLight)) return false;

  // All good
  return true;
};
